import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { AgrupaPayload, AgrupaRuntime } from '../../core/types/payloads/agrupa.types';
import { applyStimulusFields } from '../../core/print/blockStimulus';
import type { RoutingMode } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';

export function validateAgrupaPayload(raw: unknown): AgrupaPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Agrupa payload must be an object');
  const data = raw as Partial<AgrupaPayload>;
  if (!data.title) throw new Error('Agrupa requires title');
  if (!Array.isArray(data.categories) || data.categories.length < 2) {
    throw new Error('Agrupa requires at least 2 categories');
  }
  if (!Array.isArray(data.items) || data.items.length < 2) {
    throw new Error('Agrupa requires at least 2 items');
  }
  const categories = data.categories.map((c, i) => ({
    id: String(c.id || `cat_${i + 1}`),
    label: String(c.label || `Categoría ${i + 1}`),
    color: c.color ? String(c.color) : undefined,
  }));
  const catIds = new Set(categories.map((c) => c.id));
  const items = data.items.map((it, i) => ({
    id: String(it.id || `item_${i + 1}`),
    text: String(it.text || '').trim(),
    categoryId: String(it.categoryId),
  }));
  if (items.some((it) => !catIds.has(it.categoryId))) {
    throw new Error('Agrupa item references unknown category');
  }
  return applyStimulusFields({ title: String(data.title), categories, items }, raw, 'agrupa');
}

export function createInitialAgrupaRuntime(_payload: AgrupaPayload, _routing: RoutingMode): AgrupaRuntime {
  return { placements: {}, finalized: {}, aggregates: { submitted: 0, correct: 0 } };
}

function scorePlacements(payload: AgrupaPayload, placements: Record<string, string>): number {
  let correct = 0;
  for (const item of payload.items) {
    if (placements[item.id] === item.categoryId) correct += 1;
  }
  return correct;
}

export const agrupaLogic: ActivityLogicPlugin<'agrupa'> = {
  mode: 'agrupa',
  validatePayload: validateAgrupaPayload,
  createInitialRuntime: createInitialAgrupaRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant) {
    if (input.action === 'PLACE') {
      const data = input.data as { itemId: string; categoryId: string };
      const prev = ctx.runtime.placements[participant.participantId] ?? {};
      const placements = {
        ...ctx.runtime.placements,
        [participant.participantId]: { ...prev, [data.itemId]: data.categoryId },
      };
      return { runtime: { ...ctx.runtime, placements } };
    }
    if (input.action === 'SUBMIT') {
      const placements = ctx.runtime.placements[participant.participantId] ?? {};
      const correct = scorePlacements(ctx.payload, placements);
      const finalized = { ...ctx.runtime.finalized, [participant.participantId]: true };
      const all = Object.keys(finalized).filter((k) => finalized[k]);
      return {
        runtime: {
          ...ctx.runtime,
          finalized,
          aggregates: {
            submitted: all.length,
            correct: all.reduce((s, pid) => s + scorePlacements(ctx.payload, ctx.runtime.placements[pid] ?? {}), 0),
          },
        },
        scores: [{ participantId: participant.participantId, delta: correct, reason: 'agrupa' }],
      };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoAgrupaPayload(): AgrupaPayload {
  // Neutro — no verbos/sustantivos de Lenguaje (fuga vía defaultPayloadForMode).
  return validateAgrupaPayload({
    title: 'Clasifica los elementos',
    categories: [
      { id: 'cat_a', label: 'Grupo A', color: '#E0D7EC' },
      { id: 'cat_b', label: 'Grupo B', color: '#D2EBD9' },
    ],
    items: [
      { id: 'i1', text: 'Elemento 1', categoryId: 'cat_a' },
      { id: 'i2', text: 'Elemento 2', categoryId: 'cat_b' },
      { id: 'i3', text: 'Elemento 3', categoryId: 'cat_a' },
      { id: 'i4', text: 'Elemento 4', categoryId: 'cat_b' },
    ],
  });
}
